import { formatCount, tt } from '@agos/shared'

import { formatCost } from './translation-usage'

/**
 * What a *re-translate everything* sweep would send, and what it would cost - FRONTEND.md §9.325,
 * the owner's A8.
 *
 * **Answered:** *"Add it, with a cost estimate and a typed confirmation."*
 *
 * **Both figures come off endpoints that already exist** - `GET /translations/stats` and
 * `GET /ai/translation-usage?range=all`. No backend change; the arithmetic is here because what can
 * be wrong is the *derivation*, not the markup.
 */

export interface LanguageStats {
  lang: string
  manual: number
  untranslatable: number
}

export interface SweepScope {
  /** How many calls a forced sweep would make. */
  rows: number
  /** Manual translations it would leave alone. */
  keptManual: number
  /** Rows an earlier model gave up on that a forced sweep re-attempts. */
  retriedUntranslatable: number
  languages: string[]
}

/**
 * The rows a forced sweep sends, from `fill`'s own skip rule.
 *
 * `fill` builds one task per discovered source per target language and skips a pair when
 * `manual || (!untranslatable && !force)`. With `force` true that collapses to **skip only the
 * manual ones** - so the count is `uniqueSources × languages − manual`, and the untranslatable rows
 * are re-attempted rather than skipped.
 *
 * **Derived from the server's own rule rather than guessed**: an estimate that said
 * `uniqueSources × languages` would over-count by the manual rows and, worse, would imply they were
 * about to be rewritten.
 */
export function sweepScope(
  uniqueSources: number,
  perLang: readonly LanguageStats[],
  activeLangs: readonly string[],
): SweepScope {
  const languages = activeLangs.filter((lang) => lang !== 'sk')
  const counted = languages.map(
    (lang) => perLang.find((row) => row.lang === lang) ?? { lang, manual: 0, untranslatable: 0 },
  )
  return {
    rows: counted.reduce((total, row) => total + Math.max(0, uniqueSources - row.manual), 0),
    keptManual: counted.reduce((total, row) => total + row.manual, 0),
    retriedUntranslatable: counted.reduce((total, row) => total + row.untranslatable, 0),
    languages,
  }
}

/**
 * What one call has cost this franchise, from its own history.
 *
 * **`null` where there is none**, and that is the important half. A constant here would be invented:
 * the per-call cost depends on tokens nobody knows before the call, and the server prices input,
 * output, cache-write and cache-read separately (`TranslationUsage.PRICING`). The franchise's own
 * average over its own calls is the only figure that is not a guess.
 */
export function costPerCall(totalCalls: number, costEur: number): number | null {
  if (!Number.isFinite(totalCalls) || totalCalls <= 0) return null
  if (!Number.isFinite(costEur) || costEur <= 0) return null
  return costEur / totalCalls
}

/** The estimate, or `null` when the franchise has no history to derive one from. */
export function estimatedCost(rows: number, perCall: number | null): number | null {
  if (perCall === null || rows <= 0) return null
  return rows * perCall
}

/**
 * What the button says before it is pressed.
 *
 * **Says *unknown* rather than nothing.** A franchise with no history still benefits from the row
 * count, and quoting it a figure derived from no calls would be worse than admitting the gap.
 */
export function sweepEstimateNote(scope: SweepScope, cost: number | null): string {
  // `formatCount` and `formatCost`, not an `Intl` of my own. Both already exist - `formatCost` in
  // this very module, for the usage panel - and a second formatter beside them is how one screen
  // ends up grouping thousands differently from the one above it. It also cost a test: the first
  // draft built its own `Intl.NumberFormat` and the assertion failed on a **non-breaking space**,
  // which is what `sk-SK` groups with.
  const rows = formatCount(scope.rows)
  if (cost === null) {
    return `${rows} ${tt('prekladov')}. ${tt('Cenu nevieme odhadnúť - tento podnik ešte neprekladal.')}`
  }
  return `${rows} ${tt('prekladov')}, ${tt('odhadom')} ${formatCost(cost)}.`
}

/**
 * What the sweep will leave alone and what it will try again.
 *
 * **The first half corrects a note of mine.** `TODO.md` recorded that a forced sweep *"would
 * overwrite the four manual overrides with the model's words"* and called that the question to settle
 * before building. It does not - `fill` skips a manual row whether or not the sweep is forced - so
 * the screen says so plainly, because a warning about a risk that does not exist teaches somebody to
 * distrust the ones that do.
 */
export function sweepScopeNote(scope: SweepScope): string {
  const parts = [
    `${tt('Jazyky')}: ${scope.languages.join(', ')}`,
    `${tt('Ručné preklady zostanú nezmenené')} (${scope.keptManual})`,
  ]
  if (scope.retriedUntranslatable > 0) {
    parts.push(`${tt('Znova sa skúsia aj nepreložiteľné')} (${scope.retriedUntranslatable})`)
  }
  return parts.join(' · ')
}

/** The word somebody types, as the bulk SMS sender asks for one. */
export const CONFIRM_WORD = 'PRELOZIT'

/**
 * Whether the typed word matches.
 *
 * Case-folded and trimmed - the guard is *deliberate intent*, not spelling under pressure, and
 * somebody with caps lock off has still deliberately typed eight letters.
 */
export function confirmsSweep(typed: string): boolean {
  return typed.trim().toUpperCase() === CONFIRM_WORD
}

/** Only the field the endpoint reads, so a save cannot carry a language list it did not choose. */
export function forceSweepPayload(): Record<string, unknown> {
  // **No `targetLangs`.** The endpoint defaults to the franchise's configured languages, which is
  // what this screen would send anyway - §9.305's reasoning, kept: sending them again would put the
  // same decision in two places.
  return { forceRetranslate: true }
}
